/**
 * Map sidebar — filters, daily-hunt list, live-player panel, pal-spawn
 * picker, and progress reset. All state remains hoisted in MapPage so
 * Leaflet refs stay co-located with the map; this component is a pure
 * presentational shell driven by callbacks.
 */
import { useMemo, useState } from "react";
import {
  Search, X, RotateCcw, Eye, EyeOff, CheckCircle2, Gem, Sparkles, Crosshair,
  ChevronLeft, ChevronRight, ChevronDown,
} from "lucide-react";
import {
  CATEGORY_COLORS, SCALE, CATEGORY_SUBTYPES, SUBTYPE_COLORS, prettySubtype,
  prettyCategory, categoryListIcon, CATEGORY_GLYPH,
} from "./markerIcons";
import { ASSETS } from "@/lib/api";

/** Category swatch — the same art the pins use, inside a ring tinted with
 *  the category colour. Falls back to the pin's glyph if a category has no
 *  art at all. */
function CategorySwatch({ category, color, visible }) {
  const [broken, setBroken] = useState(false);
  const src = categoryListIcon(ASSETS, category);
  return (
    <span
      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border"
      style={{
        borderColor: color,
        background: "rgba(6,3,10,0.6)",
        boxShadow: visible ? `0 0 6px ${color}77` : "none",
      }}
    >
      {src && !broken ? (
        <img
          src={src}
          alt=""
          className="w-[13px] h-[13px] object-contain"
          onError={() => setBroken(true)}
        />
      ) : (
        <span className="text-[9px] leading-none" style={{ color }}>
          {CATEGORY_GLYPH[category] || "•"}
        </span>
      )}
    </span>
  );
}
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";

/** Chip list for a category's sub-types (resource node types, fishing
 *  tiers). Multi-select: every chip is on by default, clicking one hides
 *  that sub-type only. */
function SubtypeChips({ category, items, hiddenSet, onToggle, onOnly, onAll }) {
  return (
    <div className="pl-7 pr-1 pb-2 pt-0.5" data-testid={`subtype-panel-${category}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider text-[var(--dim)]">
          {CATEGORY_SUBTYPES[category]?.label}
        </span>
        <button
          onClick={onAll}
          className="text-[10px] text-[var(--muted)] hover:text-[var(--teal)] transition-colors"
          data-testid={`subtype-all-${category}`}
        >
          reset
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((s) => {
          const on = !hiddenSet.has(s.value);
          const dot = SUBTYPE_COLORS[category]?.[s.value] || CATEGORY_COLORS[category];
          return (
            <button
              key={s.value}
              onClick={(e) => (e.altKey || e.metaKey ? onOnly(s.value) : onToggle(s.value))}
              className={`flex items-center gap-1 px-1.5 py-1 rounded text-[10px] border transition-colors ${
                on
                  ? "border-white/15 bg-white/[0.04] text-[var(--bone)]"
                  : "border-white/5 text-[var(--dim)] line-through"
              } hover:border-[var(--teal-soft)]`}
              data-testid={`subtype-chip-${category}-${s.value}`}
              title="Click to toggle. Alt/Option-click to show only this one."
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: dot, opacity: on ? 1 : 0.3 }}
              />
              {prettySubtype(s.value, category)}
              <span className="text-[var(--dim)]">{s.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MapSidebar({
  // Layout
  sidebarOpen, setSidebarOpen,
  // Desktop collapse — when true, the pinned sidebar shrinks to a
  // thin icon rail (56 px wide) so the map gets more horizontal
  // space for analysis. Ignored on mobile (the drawer pattern there
  // already gives 100 % of the map back when closed).
  sidebarCollapsed = false,
  setSidebarCollapsed,
  // Search + bulk toggles
  search, setSearch, showAll, hideAll,
  hideCollected, setHideCollected,
  // Daily hunt
  user, hunt, flyToHunt,
  // Categories
  categories, hidden, toggleCat, soloCategory,
  // Sub-type filters (resource node types, fishing tiers)
  subtypeGroups = {}, subHidden = {}, toggleSubtype, soloSubtype, resetSubtypes,
  // Live players
  liveServers, liveOn, setLiveOn, liveServer, setLiveServer,
  liveStatus, livePlayers, mapRef,
  // Active raid count on the currently-selected server (drives the
  // "X active raid" pill above the server dropdown so players know
  // a boss is up before they pan around).
  activeRaidCount = 0,
  // "Pan to me" — visitor's own linked Palworld character (or null)
  myLiveCharacter, panToMe,
  // Guild base camps
  basesOn, setBasesOn, bases,
  // PalDefender server zones (safe / no-build / PvP polygons)
  zonesOn, setZonesOn, zones = [], zonesAvailable = false,
  // Pal spawns
  spawnPals, spawnPal, setSpawnPal,
  spawnCycle, setSpawnCycle, spawnMarkers,
  // Reset
  resetProgress,
}) {
  // The categories list rebuilds on every parent render (map pan, zoom,
  // hover, marker click…). Memoize so React stops re-filtering the
  // entire array each time — keeps the sidebar snappy on phones where
  // many UI events fire in quick succession.
  const visibleCategoryRows = useMemo(
    () => (categories || []).filter((c) => c.category !== "pal_spawn"),
    [categories],
  );

  // Which category's sub-type chips are expanded (one at a time).
  const [openSub, setOpenSub] = useState(null);

  // Desktop-only "collapsed" rail — when the user clicks the chevron
  // to shrink the sidebar, we render a 56 px icon strip showing just
  // a "expand" affordance + the active-raid badge (so a live raid
  // never disappears from view). All other filters move into the
  // expanded panel. Mobile drawer ignores this mode entirely.
  if (sidebarCollapsed) {
    return (
      <aside
        className="hidden lg:flex absolute top-0 left-0 bottom-0 w-14 z-[440] bg-[rgba(6,3,10,0.96)] border-r border-[var(--teal-soft)] backdrop-blur-md flex-col items-center py-4 gap-3"
        data-testid="map-sidebar-collapsed"
      >
        <button
          onClick={() => setSidebarCollapsed?.(false)}
          className="h-9 w-9 rounded-md flex items-center justify-center text-[var(--bone)] hover:text-[var(--teal)] hover:bg-[rgba(0,229,255,0.06)] transition-colors"
          data-testid="map-sidebar-expand"
          title="Expand sidebar"
          aria-label="Expand sidebar"
        >
          <ChevronRight size={18} />
        </button>
        {activeRaidCount > 0 && (
          <button
            onClick={() => setSidebarCollapsed?.(false)}
            className="h-9 w-9 rounded-md flex items-center justify-center text-rose-100 bg-gradient-to-b from-rose-700/50 to-amber-700/30 border border-rose-500/40 animate-pulse"
            title={`${activeRaidCount} active raid${activeRaidCount > 1 ? "s" : ""} — click to expand`}
            aria-label="Active raid"
          >
            ⚔︎
          </button>
        )}
      </aside>
    );
  }

  return (
    <aside
      // Width strategy:
      //   • Mobile (< lg): drawer pulled in from the left, covers
      //     ~88 % of the viewport with a 12 % strip of map peeking
      //     through (gives spatial context). Capped at 360 px so
      //     tablets don't get an awkwardly wide column.
      //   • Desktop (≥ lg): pinned 288 px column (`lg:w-72`).
      // The slide-in animation uses `translate-x` so it composites
      // on the GPU and stays smooth on mid-range Android.
      // pb-28 on phones: leaves clearance under the last control (Reset
      // Progress) so it never sits flush against the bottom edge / under
      // any browser chrome. z-[440] sits above the map chrome (410) and
      // the backdrop (430).
      className={`absolute top-0 left-0 bottom-0 w-[min(88vw,360px)] lg:w-72 z-[440] bg-[rgba(6,3,10,0.96)] border-r border-[var(--teal-soft)] backdrop-blur-md p-5 pb-28 lg:pb-5 overflow-y-auto transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      data-testid="map-sidebar"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg" style={{ fontFamily: "'Cinzel Decorative', serif" }}>World Map</h2>
        <div className="flex items-center gap-1">
          {/* Desktop: collapse-to-rail toggle */}
          <button
            className="hidden lg:flex h-8 w-8 rounded-md items-center justify-center text-[var(--muted)] hover:text-[var(--teal)] hover:bg-[rgba(0,229,255,0.06)] transition-colors"
            onClick={() => setSidebarCollapsed?.(true)}
            data-testid="map-sidebar-collapse"
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft size={16} />
          </button>
          {/* Mobile: close-drawer button */}
          <button className="lg:hidden btn btn-sm btn-secondary" onClick={() => setSidebarOpen(false)} aria-label="Close filters"><X size={14} /></button>
        </div>
      </div>

      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none z-10" />
        <input className="input !pl-10" placeholder="Search markers…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="map-search" />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <button onClick={showAll} className="btn btn-sm btn-secondary" data-testid="show-all-button">
          <Eye size={11} /> Show All
        </button>
        <button onClick={hideAll} className="btn btn-sm btn-secondary" data-testid="hide-all-button">
          <EyeOff size={11} /> Hide All
        </button>
      </div>
      {user && (
        <button
          onClick={() => setHideCollected((v) => !v)}
          className={`btn btn-sm w-full mb-4 ${hideCollected ? "" : "btn-secondary"}`}
          data-testid="hide-collected-toggle"
        >
          <CheckCircle2 size={11} /> {hideCollected ? "Showing only undiscovered" : "Hide collected"}
        </button>
      )}

      {/* === Live Players + Guild Bases ===
          Pulled to the top of the sidebar so the on/off toggles are
          one click away — these are the two most frequently-flipped
          controls. Their expanded panels (player list, base count)
          render in-place underneath the toggles. */}
      {liveServers.length > 0 && (
        <div className="mb-5 pb-4 border-b border-white/5" data-testid="live-players-section">
          <div className="flex items-center justify-between mb-2">
            <h3 className="label !mb-0 flex items-center gap-2">
              {liveOn && liveStatus?.online && <span className="live-pulse-dot" />}
              Live Players
            </h3>
            <button
              onClick={() => setLiveOn(!liveOn)}
              className={`btn btn-sm ${liveOn ? "" : "btn-secondary"}`}
              data-testid="live-toggle"
            >
              {liveOn ? "On" : "Off"}
            </button>
          </div>
          {liveServers.length > 1 && (
            <select
              className="input w-full mb-2 text-sm"
              value={liveServer}
              onChange={(e) => setLiveServer(e.target.value)}
              data-testid="live-server-select"
            >
              {liveServers.map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
            </select>
          )}
          {activeRaidCount > 0 && (
            <button
              onClick={() => {
                // Find the first raid marker on the map and fly to it.
                const layer = document.querySelector(".leaflet-raid-marker");
                if (layer && mapRef?.current) {
                  // Trigger the marker's popup. We rely on Leaflet's
                  // built-in event delegation rather than reaching
                  // into refs we don't own — keeps coupling low.
                  layer.click();
                }
              }}
              className="w-full mb-2 px-2.5 py-1.5 rounded-md bg-gradient-to-r from-rose-700/40 to-amber-700/30 border border-rose-500/40 text-xs uppercase tracking-wider font-bold text-rose-100 hover:from-rose-700/60 hover:to-amber-700/50 transition-colors flex items-center justify-between"
              data-testid="active-raid-pill"
              title="Click to jump to the active raid"
            >
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                {activeRaidCount} active raid{activeRaidCount > 1 ? "s" : ""}
              </span>
              <span className="opacity-70">→ Fly</span>
            </button>
          )}
          {liveOn && liveStatus && (
            <div className="text-xs space-y-1" data-testid="live-status">
              {liveStatus.online ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-[var(--muted)]">Players:</span>
                    <span className="glow-teal">{liveStatus.player_count}/{liveStatus.max_players}</span>
                  </div>
                  {liveStatus.fps > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted)]">Server FPS:</span>
                      <span>{liveStatus.fps}</span>
                    </div>
                  )}
                  {/* "Pan to me" — only available when the visitor's
                      linked character is currently online on the active
                      server. Disabled state keeps the button visible so
                      players know the feature exists. */}
                  {user && (
                    <button
                      onClick={panToMe}
                      disabled={!myLiveCharacter}
                      className={`btn btn-sm w-full mt-2 ${myLiveCharacter ? "" : "btn-secondary opacity-60 cursor-not-allowed"}`}
                      title={myLiveCharacter ? `Fly to ${myLiveCharacter.name}` : "Your character isn't online (or hasn't been linked yet)"}
                      data-testid="pan-to-me-button"
                    >
                      <Crosshair size={12} /> {myLiveCharacter ? `Pan to me (Lv ${myLiveCharacter.level})` : "Pan to me · offline"}
                    </button>
                  )}
                  {livePlayers.length > 0 && (
                    <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                      {livePlayers.map((p) => (
                        <li
                          key={p.name + p.user_id}
                          className="flex items-center justify-between px-2 py-1 min-h-11 lg:min-h-0 bg-black/30 rounded cursor-pointer hover:bg-white/5"
                          onClick={() => mapRef.current?.flyTo([-p.tile_y * SCALE, p.tile_x * SCALE], 4, { duration: 1.0 })}
                          data-testid={`live-player-row-${p.name}`}
                          title="Click to pan the map to this player"
                        >
                          <span className="flex items-center gap-1.5 truncate">
                            <span className="live-pulse-dot live-pulse-dot-sm" />
                            <span className="truncate">{p.name}</span>
                          </span>
                          <span className="text-[var(--muted)]">Lv {p.level}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <div className="text-[var(--muted)] italic">Server offline</div>
              )}
            </div>
          )}
          {liveOn && !liveStatus && (
            <div className="text-[11px] text-[var(--muted)] italic">Connecting…</div>
          )}
          {!liveOn && (
            <p className="text-[11px] text-[var(--muted)]">
              Toggle on to see player dots on the map (5 s refresh).
            </p>
          )}

          {/* Guild base camps — opt-in because exportguilds + SFTP fetch is slow. */}
          <div className="mt-4 pt-3 border-t border-white/5" data-testid="bases-section">
            <div className="flex items-center justify-between mb-1">
              <h4 className="label !mb-0 flex items-center gap-2">
                <span
                  className="inline-block w-2.5 h-2.5"
                  style={{ background: "#ff9a3c", borderRadius: 2, boxShadow: "0 0 6px #ff9a3c" }}
                />
                Guild Bases
              </h4>
              <button
                onClick={() => setBasesOn(!basesOn)}
                className={`btn btn-sm ${basesOn ? "" : "btn-secondary"}`}
                data-testid="bases-toggle"
              >
                {basesOn ? "On" : "Off"}
              </button>
            </div>
            {basesOn ? (
              <p className="text-[11px] text-[var(--muted)]">
                Showing <span className="glow-teal">{bases.length}</span> base camp{bases.length === 1 ? "" : "s"} on the map. Refreshes every 5 min.
              </p>
            ) : (
              <p className="text-[11px] text-[var(--muted)]">
                Pulls guild base locations from the server via RCON.
              </p>
            )}
          </div>

          {/* Server rule zones. Only rendered when the active server actually
              has a zones file — a toggle that does nothing is worse than no
              toggle. Areas, not pins, so it gets its own control rather than
              sitting in the category list. */}
          {zonesAvailable && (
            <div className="mt-4 pt-3 border-t border-white/5" data-testid="zones-section">
              <div className="flex items-center justify-between mb-1">
                <h4 className="label !mb-0 flex items-center gap-2">
                  <span
                    className="inline-block w-2.5 h-2.5"
                    style={{ background: "#2A9D8F", borderRadius: 2, boxShadow: "0 0 6px #2A9D8F" }}
                  />
                  Server Zones
                </h4>
                <button
                  onClick={() => setZonesOn(!zonesOn)}
                  className={`btn btn-sm ${zonesOn ? "" : "btn-secondary"}`}
                  data-testid="zones-toggle"
                >
                  {zonesOn ? "On" : "Off"}
                </button>
              </div>
              {zonesOn ? (
                <>
                  <p className="text-[11px] text-[var(--muted)] mb-1.5">
                    Showing <span className="glow-teal">{zones.length}</span> zone{zones.length === 1 ? "" : "s"}. Hover a shape for its rules.
                  </p>
                  {/* Legend — colours are derived from permissions, so this is
                      the only place the mapping is explained. */}
                  <ul className="space-y-0.5">
                    {[...new Map(zones.map((z) => [z.kind, z])).values()].map((z) => (
                      <li key={z.kind} className="flex items-center gap-1.5 text-[10px] text-[var(--muted)]">
                        <span
                          className="inline-block w-2 h-2 rounded-sm shrink-0"
                          style={{ background: z.color, opacity: 0.8 }}
                        />
                        {z.kind_label}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-[11px] text-[var(--muted)]">
                  Safe zones, no-build areas and PvP regions set by server rules.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {user && hunt && (
        <div className="mb-5 pb-4 border-b border-white/5" data-testid="daily-hunt-section">
          <div className="flex items-center justify-between mb-2">
            <h3 className="label !mb-0 flex items-center gap-1.5">
              <Gem size={12} className="text-[#FFD700]" />
              Daily Treasure Hunts
            </h3>
            <span
              className="text-[10px] text-[var(--muted)]"
              title="Streak multiplier — climbs from 1.0× to 2.0× over 7 days"
            >
              Day {hunt.streak_day} · ×{hunt.streak_multiplier}
            </span>
          </div>
          {!hunt.linked ? (
            <div
              className="text-[11px] text-[var(--muted)] italic px-2 py-2 bg-black/30 rounded"
              data-testid="hunt-unlinked-notice"
            >
              Link your in-game character (Account → Linking) to unlock auto-claim rewards.
            </div>
          ) : (
            <>
              <p className="text-[11px] text-[var(--muted)] mb-2">
                Visit each spot in-game (within {hunt.radius_meters}m) to auto-claim. Top of the map shows your hunts.
              </p>
              <ul className="space-y-1.5" data-testid="hunt-list">
                {hunt.hunts.map((h, i) => (
                  <li key={h.marker_id}>
                    <button
                      onClick={() => flyToHunt(h)}
                      className={`flex items-center justify-between w-full px-2 py-1.5 min-h-11 lg:min-h-0 rounded text-sm transition-colors hover:bg-white/5 ${h.claimed ? "opacity-60" : ""}`}
                      data-testid={`hunt-item-${i}`}
                      title="Pan map to this hunt"
                    >
                      <span className="flex items-center gap-2 truncate">
                        {h.claimed
                          ? <CheckCircle2 size={12} className="text-[#7FE3C4] flex-shrink-0" />
                          : <Sparkles size={12} className="text-[#FFD700] flex-shrink-0" />}
                        <span className="truncate">{h.claimed ? <span className="line-through">{h.name}</span> : h.name}</span>
                      </span>
                      <span className="text-[10px] text-[var(--muted)] flex-shrink-0 ml-2 inline-flex items-center gap-1">
                        {h.reward_skulls}<ChilletCoinIcon size={10}/>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {hunt.all_done && (
                <div
                  className={`mt-2 text-[11px] px-2 py-1.5 rounded text-center ${hunt.bonus_claimed ? "bg-[#7FE3C4]/10 text-[#7FE3C4]" : "bg-[#FFD700]/10 text-[#FFD700]"}`}
                  data-testid="hunt-bonus-status"
                >
                  {hunt.bonus_claimed
                    ? <>All 3 found — bonus +{hunt.bonus_reward}<ChilletCoinIcon size={11}/> claimed!</>
                    : <>All 3 visited — claiming +{hunt.bonus_reward}<ChilletCoinIcon size={11}/> bonus…</>}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <h3 className="label !mb-0">Categories</h3>
        <span className="hidden lg:inline text-[10px] text-[var(--muted)]">click=toggle · ⌥click=solo</span>
      </div>
      <ul className="space-y-1.5">
        {visibleCategoryRows.map((c) => {
          const visible = !hidden.has(c.category);
          const color = CATEGORY_COLORS[c.category] || "#F9F6EE";
          const subItems = subtypeGroups[c.category] || null;
          const hiddenSet = subHidden[c.category] || new Set();
          const filteredCount = hiddenSet.size > 0
            ? subItems.filter((s) => !hiddenSet.has(s.value)).reduce((n, s) => n + s.count, 0)
            : null;
          return (
            <li key={c.category}>
              <div className="flex items-stretch">
                <button
                  onClick={(e) => {
                    if (e.altKey || e.metaKey) { soloCategory(c.category); return; }
                    toggleCat(c.category);
                    // Turning a filterable category on reveals its chips
                    // straight away; turning it off tidies them away again.
                    if (subItems) setOpenSub(visible ? null : c.category);
                  }}
                  className={`flex items-center justify-between flex-1 min-w-0 px-2 min-h-11 lg:min-h-0 py-1.5 rounded text-sm transition-colors ${visible ? "" : "opacity-40"} hover:bg-white/5`}
                  data-testid={`map-cat-${c.category}`}
                  title="Click to toggle. Alt/Option-click to show only this category."
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {visible
                      ? <Eye size={12} className="text-[var(--muted)] flex-shrink-0" />
                      : <EyeOff size={12} className="text-[var(--dim)] flex-shrink-0" />}
                    <CategorySwatch category={c.category} color={color} visible={visible} />
                    <span className="capitalize truncate">{prettyCategory(c.category)}</span>
                  </span>
                  <span className="text-xs text-[var(--muted)] flex-shrink-0 ml-2">
                    {filteredCount !== null
                      ? <span className="glow-teal">{filteredCount}</span>
                      : (user ? `${c.found || 0}/${c.total}` : c.total)}
                  </span>
                </button>
                {subItems && (
                  <button
                    onClick={() => setOpenSub((p) => (p === c.category ? null : c.category))}
                    className={`px-1.5 rounded text-[var(--muted)] hover:text-[var(--teal)] hover:bg-white/5 transition-colors ${openSub === c.category ? "text-[var(--teal)]" : ""}`}
                    data-testid={`subtype-expand-${c.category}`}
                    title={`Filter by ${CATEGORY_SUBTYPES[c.category].label.toLowerCase()}`}
                    aria-label={`Filter ${c.category} by ${CATEGORY_SUBTYPES[c.category].label}`}
                  >
                    <ChevronDown
                      size={14}
                      className="transition-transform"
                      style={{ transform: openSub === c.category ? "rotate(180deg)" : "none" }}
                    />
                  </button>
                )}
              </div>
              {subItems && openSub === c.category && (
                <SubtypeChips
                  category={c.category}
                  items={subItems}
                  hiddenSet={hiddenSet}
                  onToggle={(v) => toggleSubtype(c.category, v)}
                  onOnly={(v) => soloSubtype(c.category, v)}
                  onAll={() => resetSubtypes(c.category)}
                />
              )}
            </li>
          );
        })}
        {categories.length === 0 && <li className="text-xs text-[var(--muted)]">No marker categories yet.</li>}
      </ul>

      {spawnPals.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/5" data-testid="pal-spawn-section">
          <h3 className="label flex items-center justify-between !mb-2">
            <span>Pal Spawns</span>
            <span className="text-[10px] text-[var(--muted)]">{spawnPals.length} pals</span>
          </h3>
          <select
            className="input w-full mb-2 text-sm"
            value={spawnPal}
            onChange={(e) => setSpawnPal(e.target.value)}
            data-testid="pal-spawn-select"
          >
            <option value="">— pick a pal —</option>
            {spawnPals.map((p) => (
              <option key={p.name} value={p.name}>{p.name} ({p.count})</option>
            ))}
          </select>
          {spawnPal && (
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { id: "", label: "Both" },
                { id: "day", label: "Day" },
                { id: "night", label: "Night" },
              ].map((c) => (
                <button
                  key={c.id || "all"}
                  onClick={() => setSpawnCycle(c.id)}
                  className={`btn btn-sm ${spawnCycle === c.id ? "" : "btn-secondary"}`}
                  data-testid={`spawn-cycle-${c.id || "both"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
          {spawnPal && (
            <div className="text-[11px] text-[var(--muted)] mt-2">
              {spawnMarkers.length} spawn point{spawnMarkers.length === 1 ? "" : "s"} shown
            </div>
          )}
        </div>
      )}

      {user && (
        <button onClick={resetProgress} className="btn btn-secondary btn-sm w-full mt-6" data-testid="map-reset-button">
          <RotateCcw size={12} /> Reset Progress
        </button>
      )}
    </aside>
  );
}
